import React, { useState, useEffect } from "react";
import { useMediaQuery } from "@chakra-ui/react";
import {
  Box,
  Button,
  Flex,
  Heading,
  Image,
  LinkOverlay,
  LinkBox,
  Text,
} from "@chakra-ui/react";
import {
  FacebookShareButton,
  FacebookIcon,
  TwitterShareButton,
  TwitterIcon,
  WhatsappShareButton,
  WhatsappIcon,
  LinkedinShareButton,
  LinkedinIcon,
  WeiboShareButton,
  WeiboIcon,
  EmailShareButton,
  EmailIcon,
} from "react-share";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, A11y } from "swiper";
import enableSwiperClicksFromNav from "../../helpers/enableSwiperClicksFromNav";
import { handleAppendArrowsToSliders } from "../../helpers/handleAppendArrowsToSliders";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/a11y";
import "./sliderPortrait.scss";
import { FilmScheduleDataType, SliderItemsType, SliderProps } from "../../types/homePageTypes";
import { parseISO, format, isBefore, isAfter, isEqual, addDays } from 'date-fns';


// poster sider element for year round home page

const SliderPortrait = ({ 
  title,
  description,
  isWebProgrammeSlider, 
  sliderItems,
  activeSeasonFilms
}: SliderProps) => {
  const [isLargerThan567] = useMediaQuery("(min-width: 567px)");
  const [isLargerThan992] = useMediaQuery("(min-width: 992px)");
  const [isLargerThan1200] = useMediaQuery("(min-width: 1200px)");
  const { swiperRef } = enableSwiperClicksFromNav();

  // create an id for the swiper component
  let swiperId: string = title ? `portrait-${title.replace(/[^a-zA-Z0-9]/g, '')}` : "portrait-0";
  if (swiperId.length > 25) {
    swiperId = swiperId.substring(0, 25);
  }

  // Is slider now playing or coming soon
  const npSlider: boolean = title === 'Now Playing' ;
  const csSlider: boolean = title === 'Coming Soon';

  //If Now playing or Coming soon slider use active season films data
  if(npSlider || csSlider) sliderItems = activeSeasonFilms!;

  // slider contains a festival film
  const isFestivalSlider = sliderItems?.some(item => item.isFestivalFilm) || false;
  const ENDPOINT = isFestivalSlider ? "/festivalfilmtemplatejson/" : "/yearroundfilmtemplatejson/";
  const film_slugs = sliderItems.map(item => item.baseSlug);

  const [filmScheduleData, setFilmScheduleData] = useState<FilmScheduleDataType | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [shareIconClick, setShareIconClick] = useState<boolean>(false)
  const [currInd, setCurrInd] = useState<number | null | undefined>(null)

  const handleShareClick: (e:React.MouseEvent<HTMLButtonElement, MouseEvent> | React.MouseEvent<HTMLAnchorElement, MouseEvent> | React.KeyboardEvent<HTMLButtonElement> | React.FocusEvent<HTMLButtonElement, Element>, ind?: number) => void = (e, ind) => {
    e.preventDefault();

    setShareIconClick(!shareIconClick);
    setCurrInd(ind);
  }

  // load film schedule item data
  useEffect(() => {

    // only load data if not now playing or coming soon sliders
    if(!npSlider && !csSlider){

      const fetchFilmScheduleData = async (slug: string) => {
        try {
          const response = await fetch(`${ENDPOINT}${slug}`);

          if (!response.ok) {
            throw new Error(`Error: ${response.statusText}`);
          }

          const json: any = await response.json();

          if (json && json['filmShowtimes']) {
            return json['filmShowtimes'];
          }

          const defaultObj = {
            buyTicketsButton: 1,
            digitalEvents: [],
            isFestivalFilm: false,
            scheduleItems: [],
            scheduleItemsByDate: [],
            scheduleItemsNoPress: []
          }
          return defaultObj;
        }

        catch (err: any) {
          setError(err.message);
          console.error(`Error fetching the data for film with slug: ${slug}:`, err.message)
          throw err; // needed for 'Promise.allSettled' to recognize that a rejection occured and report it
        }
      };

      const fetchAllSliderFilmsScheduleData = async () => {
        const results = await Promise.allSettled(film_slugs.map(fetchFilmScheduleData));

        const allSliderFilmsData: any[] = [];
        const allSliderFilmsErrors: any[] = [];

        results.forEach((result, index) => {
          if (result.status === "fulfilled") {
            allSliderFilmsData.push(result.value);
          } else {
            allSliderFilmsErrors.push(`Error fetching data for id ${film_slugs[index]}: ${result.reason}`);
          }
        });

        setFilmScheduleData(allSliderFilmsData);
        if (allSliderFilmsErrors.length > 0) {
          setError(allSliderFilmsErrors.join(' | '));
        }
        setLoading(false);
      };

      fetchAllSliderFilmsScheduleData();
    
    }
  }, []);

  // load Tiffr script
  // useEffect(() => {
  //   const script = document.createElement('script');
  //   script.src = "https://tiffr.com/assets/widget.js";
  //   script.async = true;
  //   script.setAttribute('data-tiffr-festival', '2023');
  //   document.body.appendChild(script);
  //   return () => {
  //     document.body.removeChild(script);
  //   }
  // }, []);


  // add slider next and previous arrows to target areas
  const nextTarget = `#${ swiperId }.portraitSlider .swiper-button-next`;
  const prevTarget = `#${ swiperId }.portraitSlider .swiper-button-prev`;
  handleAppendArrowsToSliders(nextTarget, prevTarget);


  if (error) {
    console.log(error);
  }


  // add film schedule data to film slider items
  if (!loading && filmScheduleData && filmScheduleData.length > 0) {
    filmScheduleData.forEach((data, index, array) => {
      let itemsByDate = data.scheduleItemsByDate ? data.scheduleItemsByDate : [];
      let firstDateInRange = "";
      let secondDateInRange = "";

      // if scheduleItemsByDate object has items
      if (itemsByDate && itemsByDate.length > 0) {

        // pull first date
        if (itemsByDate[0].date) {
          firstDateInRange = format( parseISO( itemsByDate[0].date ), "EEE, MMM. d");
        }

        // pull last date if exists
        if (itemsByDate.length > 1) {
          if (itemsByDate[ itemsByDate.length - 1 ].date) {
            secondDateInRange = format( parseISO( itemsByDate[ itemsByDate.length - 1 ].date ), "EEE, MMM. d");
          }
        }
      }
      sliderItems[index]["firstDateInRange"] = firstDateInRange;
      sliderItems[index]["secondDateInRange"] = secondDateInRange;
    })
  }

  // filter items if now playng or coming soon slider
  if(npSlider || csSlider){
    const weekFromNow: Date = addDays(new Date(), 7);
    sliderItems = sliderItems.filter(item => {
      const itemStartDate = parseISO(item.firstDateInRange!);
      if(
        (npSlider && (isBefore( itemStartDate, weekFromNow) || isEqual( itemStartDate, weekFromNow))) ||
        (csSlider && isAfter( itemStartDate, weekFromNow)) 
      ) return true; 
      else return false;
    })

    // show new realeases items first and then cinematheque items
    const newReleasesItems: SliderItemsType[] = [];
    const cinemathequeItems: SliderItemsType[] = [];
    sliderItems.forEach(item => {
      if(item.webProgramme && item.webProgramme.length){
        if(item.webProgramme.some(webP => webP.title === "New Releases")) newReleasesItems.push(item);
        if(item.webProgramme.some(webP => webP.cinematheque === 1)) cinemathequeItems.push(item);
      }
    })
    sliderItems = newReleasesItems.concat(cinemathequeItems);

  }

  return (
    <Box 
      marginBottom={{ base: "50px !important", sm: "80px !important" }}
      marginTop="0 !important"
    >
      {/* section title */}
      <Heading
        as="h2"
        size={{ base: "section-header-mobile", sm: "section-header-tablet", lg: "section-header-desktop" }} 
        marginBottom={{ base: "12px !important", sm: "20px !important" }}
        marginLeft={{ base: "24px !important", sm: "28px !important" }}
      >
        { title }
      </Heading>

      {/* section description */}
      <Text
        size={{ base: "bravo-mobile", sm: "bravo", lg: "bravo-desktop" }}
        color="black !important"
        marginTop="0"
        marginBottom={{ base: "12px !important", sm: "20px !important" }}
        marginLeft={{ base: "24px !important", sm: "28px !important" }}
        p="0 30px 0 0"
      >
        { description }
      </Text>

      {/* mobile and tablet view */}
        {!isLargerThan1200 &&
          <>
            <Swiper
              id={ swiperId }
              className="portraitSlider"
              modules={[ A11y ]}
              slidesPerView={ isLargerThan567 ? ( isLargerThan992 ? 2.66 : 2.36 ) : 2.409 }
              spaceBetween={ isLargerThan567 ? 12 : 10 }
            >
              {sliderItems.map(( slide, index ) => {
                let formattedFirstDate = slide.firstDateInRange;
                let formattedSecondDate = slide.secondDateInRange;
                // change date format if now playing or coming soon sliders
                if(npSlider || csSlider){
                  if(formattedFirstDate) formattedFirstDate = format( parseISO(formattedFirstDate!), "EEE, MMM. d");
                  if(formattedSecondDate) formattedSecondDate = format( parseISO(formattedSecondDate!), "EEE, MMM. d");
                }
                return (
                  <SwiperSlide key={ index } className={ currInd === index ? "swiper-active-slide" : "swiper-inactive-slide"}>
                    <LinkBox>
                      <LinkOverlay href={ slide.slug } aria-label={ slide.buttonText }>
                        <Image
                          height="calc(27.75vw * 1.49)"
                          src={ slide.posterUrl }
                          alt={ slide.posterAlt }
                          boxSize="full"
                          objectFit="cover"
                          border="1px solid #CCC"
                        />
                      </LinkOverlay>
                    </LinkBox>
                    <Flex
                      w="100%"
                      p={{ base: "23px", sm: "30px" }}
                      direction="column"
                      justifyContent="flex-start !important"
                    >
                      {/* date range */}
                      <Heading
                        as="h3"
                        size={{ base: "portrait-slider-header-mobile", sm: "portrait-slider-header-tablet" }}
                        marginBottom={{ base: "16px", sm: "20px" }}
                        textAlign="left"
                        aria-label={`Screenings for film ${ slide.title }: ${ formattedFirstDate } ${ formattedSecondDate ? "to" : ""} ${ formattedSecondDate }`}
                      >
                        {!formattedFirstDate && 
                          <>No current showtimes</>
                        }
                        {formattedFirstDate &&
                          <>
                            { formattedFirstDate }
                            { formattedSecondDate && formattedFirstDate !== formattedSecondDate &&
                              <>{" "}-<br></br>{ formattedSecondDate }</>
                            }
                          </>
                        }
                      </Heading>

                      {/* button */}
                      <LinkBox>
                        <Button
                          size={{ base: "poster-slider-mobile", sm: "poster-slider" }}
                          marginTop={{ base: "14px !important", sm: "0px !important" }}
                          marginBottom={{ base: "20px !important", sm: "36px !important" }}
                        >
                          <LinkOverlay href={ slide.slug }>
                            {!formattedFirstDate &&
                              <>Learn more</>
                            }
                            {formattedFirstDate &&
                              <>See Showtimes</>
                            }
                          </LinkOverlay>
                        </Button>
                      </LinkBox>

                      {/* icons */}
                      <Flex
                        direction= "row"
                        gap={{ base: "16px", sm: "21px" }}
                        alignItems={{ base: "center", sm: "flex-start" }}
                        justifySelf= "flex-end"
                      >
                        {/* TIFFR */}
                        {/* <Button
                          className="tiffr-shortlist" 
                          data-show-id={slide.id} 
                          style={{
                            minWidth: "unset",
                            margin: 0,
                            padding: 0,
                            border: "none",
                            background: "none",
                          }}
                          _hover={{ 
                            bgColor: "unset !important" 
                          }}
                        >
                        </Button> */}

                        {/* share icon */}                       
                        <a className={"shareLink"} href="#" onClick={(e) => handleShareClick(e, index)}>
                          <Image 
                            className={"shareSvgImg"} 
                            h="23px" 
                            w="21px"
                            src="/img/Share.svg" 
                            alt="Share icon" 
                          />
                        </a>

                        {/* share window */}
                        {shareIconClick && index === currInd &&
                          <div className={"shareContainer"}>
                              <div className={"shareInnerContainer"}>
                                  <div className={"shareContainerArrow"}></div>
                                  <FacebookShareButton url={`https://tiff.net/${slide.slug}`}>
                                      <FacebookIcon size={50} />
                                  </FacebookShareButton>
                                  <TwitterShareButton url={`https://tiff.net/${slide.slug}`}>
                                      <TwitterIcon size={50} />
                                  </TwitterShareButton>
                                  <WhatsappShareButton url={`https://tiff.net/${slide.slug}`}>
                                      <WhatsappIcon size={50} />
                                  </WhatsappShareButton>
                                  <LinkedinShareButton url={`https://tiff.net/${slide.slug}`}>
                                      <LinkedinIcon size={50} />
                                  </LinkedinShareButton>
                                  <WeiboShareButton url={`https://tiff.net/${slide.slug}`}>
                                      <WeiboIcon size={50} />
                                  </WeiboShareButton>
                                  <EmailShareButton url={`https://tiff.net/${slide.slug}`} >
                                      <EmailIcon size={50} />
                                  </EmailShareButton>

                                  <button className={"shareClose"} onClick={(e) => handleShareClick(e)} onKeyDown={(e) => handleShareClick(e)} onBlur={(e) => handleShareClick(e)}>
                                      <i className='material-icons'>close</i>
                                  </button>
                              </div>
                          </div>
                        }

                      </Flex>

                    </Flex>
                  </SwiperSlide>
                )
              })}
            </Swiper>

            {/* divider */}
            <Box
              backgroundColor="#000000"
              h={{ base: "5px", sm: "10px" }}
              w="100%"
              marginLeft="10px" 
            ></Box>
          </>
        }

      {/* desktop view */}
        {isLargerThan1200 &&
          <Swiper
            id={ swiperId }
            onSwiper={( swiperInstance ) => { swiperRef.current = swiperInstance }}
            className="portraitSlider"
            modules={[ Navigation, A11y ]}
            navigation={ true }
            slidesPerView={ 3.518 }
            spaceBetween={ 11 }
          >
            {sliderItems.map(( slide, index ) => {
              let formattedFirstDate = slide.firstDateInRange;
              let formattedSecondDate = slide.secondDateInRange;
              // change date format if now playing or coming soon sliders
              if(npSlider || csSlider){
                if(formattedFirstDate) formattedFirstDate = format( parseISO(formattedFirstDate!), "EEE, MMM. d");
                if(formattedSecondDate) formattedSecondDate = format( parseISO(formattedSecondDate!), "EEE, MMM. d");
              }
              return (
                <SwiperSlide key={ index } className={ currInd === index ? "swiper-active-slide" : "swiper-inactive-slide"}>
                  <Image
                    src={ slide.posterUrl }
                    alt={ slide.posterAlt }
                    height="calc(27.75vw * 1.49)"
                    objectFit="cover"
                    transition="0.3s ease-in-out"
                    border="1px solid #CCC"
                  />
                  <LinkBox
                    display="flex"
                    position="absolute"
                    top="0"
                    left="0"
                    flexDirection="column"
                    justifyContent="center !important"
                    h="100%"
                    w="100%"
                    p={{ md: "30px 37px 50px 37px", "2xl": "45px 56px" }}
                    bg="rgba(0, 0, 0, 0.3)"
                    opacity="0"
                    _hover={{
                      opacity: "1",
                      transform: "translateX(0)"
                    }}
                    _focusWithin={{
                      opacity: "1",
                      transform: "translateX(0)"
                    }}
                    transition="0.3s ease-in-out, transform 0.3s ease-out"
                    backdropFilter="blur(2px)"
                    transform="translateX(-5px)"
                  >
                    <LinkOverlay href={ slide.slug } aria-label={ slide.buttonText }>
                      {/* date range */}
                      <Heading
                        as="h3"
                        size={{ lg: "portrait-slider-header-tablet", xl: "portrait-slider-header-desktop" }}
                        marginBottom="16px !important"
                        // marginTop={{ base: "40% !important", "2xl": "50% !important", bd3: "60% !important" }}
                        textAlign="left"
                        aria-label={`Screenings for film ${ slide.title }: ${ formattedFirstDate } ${ formattedSecondDate ? "to" : ""} ${ formattedSecondDate }`}
                      >
                        {!formattedFirstDate && 
                          <>No current showtimes</>
                        }
                        {formattedFirstDate &&
                          <>
                            { formattedFirstDate }
                            { formattedSecondDate && formattedFirstDate !== formattedSecondDate && 
                              <>{" "}-<br></br>{ formattedSecondDate }</>
                            }
                          </>
                        }
                      </Heading>

                      {/* button */}
                      <Box flexGrow="1">
                        <Button
                          size={{ "md": "poster-slider" }}
                          _hover={{ bgColor: "#000000 !important" }}
                        >
                          {!formattedFirstDate &&
                            <>Learn more</>
                          }
                          {formattedFirstDate &&
                            <>See Showtimes</>
                          }
                        </Button>
                      </Box>

                      {/* TIFFR */}
                      {/* <Button 
                        className="tiffr-shortlist" 
                        data-show-id={slide.id} 
                        style={{
                          position: "absolute",
                          bottom: "36px",
                          minWidth: "unset",
                          margin: 0,
                          padding: 0,
                          border: "none",
                          background: "none",
                        }}
                        _hover={{ 
                          bgColor: "unset !important" 
                        }}
                        left={{xl: "38px", "2xl": "57px" }}
                      >
                      </Button> */}

                      {/* share icon */}                       
                      <Button 
                        className={"shareLink"} 
                        onClick={(e) => handleShareClick(e, index)}
                        position="absolute"
                        bottom="29px"
                        background="transparent"
                        m="0"
                        p="0"
                        border="none"
                        _hover={{
                          background: "none !important",
                          border: "none !important",
                        }}
                        _focus={{
                          border: "1px solid white !important",
                        }}
                      >
                        <Image 
                          className={"shareSvgImg"} 
                          h="20px" 
                          w="20px" 
                          src="/img/Share.svg" 
                          alt="Share icon" 
                        />
                      </Button>

                      {/* share window */}
                      {shareIconClick && index === currInd &&
                        <div className={"shareContainer"}>
                            <div className={"shareInnerContainer"}>
                                <div className={"shareContainerArrow"}></div>
                                <FacebookShareButton url={`https://tiff.net/${slide.slug}`}>
                                    <FacebookIcon size={50} />
                                </FacebookShareButton>
                                <TwitterShareButton url={`https://tiff.net/${slide.slug}`}>
                                    <TwitterIcon size={50} />
                                </TwitterShareButton>
                                <WhatsappShareButton url={`https://tiff.net/${slide.slug}`}>
                                    <WhatsappIcon size={50} />
                                </WhatsappShareButton>
                                <LinkedinShareButton url={`https://tiff.net/${slide.slug}`}>
                                    <LinkedinIcon size={50} />
                                </LinkedinShareButton>
                                <WeiboShareButton url={`https://tiff.net/${slide.slug}`}>
                                    <WeiboIcon size={50} />
                                </WeiboShareButton>
                                <EmailShareButton url={`https://tiff.net/${slide.slug}`} >
                                    <EmailIcon size={50} />
                                </EmailShareButton>

                                <button className={"shareClose"} onClick={(e) => handleShareClick(e)} onKeyDown={(e) => handleShareClick(e)} onBlur={(e) => handleShareClick(e)}>
                                    <i className='material-icons'>close</i>
                                </button>
                            </div>
                        </div>
                      }
                    </LinkOverlay>
                  </LinkBox>
                </SwiperSlide>
              )
            })}  
          </Swiper>
        }
    </Box>
  );
}

export default SliderPortrait;
